import { DressingWeight, type MixedDressingDetails } from '@sg/graphql-schema';

import { type Modification } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

export function generateMixedDressingDetailsFactory(params: Params) {
  const { hasMultipleMixableDressings, currentMixedDressingDetails, defaultWeight } = params; // prettier-ignore

  const defaultDressingWeight = getDefaultDressingWeight(defaultWeight, hasMultipleMixableDressings); // prettier-ignore

  return (modification: Modification) => {
    const maybeDressingDetail = currentMixedDressingDetails.find(
      ({ ingredientId }) => ingredientId === modification.ingredient?.id,
    );

    if (maybeDressingDetail) {
      return maybeDressingDetail;
    }

    return generateMixedDressingDetails(modification, defaultDressingWeight);
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the default dressing weight to use when generating mixed dressing
 * details.
 *
 * - If a default weight is provided, it will be used.
 * - If there are multiple mixable dressings, the default weight will be "light".
 * - If there is only one mixable dressing, the default weight will be "medium".
 */
function getDefaultDressingWeight(
  defaultWeight: DressingWeight | undefined,
  hasMultipleMixableDressings: boolean,
) {
  if (defaultWeight) {
    return defaultWeight;
  }

  if (hasMultipleMixableDressings) {
    return DressingWeight.Light;
  }

  return DressingWeight.Medium;
}

function generateMixedDressingDetails(
  modification: Modification,
  defaultWeight: DressingWeight,
): MixedDressingDetails | null {
  const { ingredient } = modification;

  if (!ingredient) {
    return null;
  }

  return { ingredientId: ingredient.id, weight: defaultWeight };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  hasMultipleMixableDressings: boolean;
  currentMixedDressingDetails: MixedDressingDetails[];
  defaultWeight?: DressingWeight;
};
